<template>
  <div>
    <div v-if="isAdmin" class="flex justify-center bg-gray-800 border-b border-gray-700 sticky top-0 z-30">
      <button
        v-for="tab in craftTabs"
        :key="tab.id"
        @click="activeTab = tab.id"
        :class="[
          'px-4 py-2 text-sm font-medium transition-colors duration-200 relative',
          activeTab === tab.id
            ? 'text-indigo-300 border-b-2 border-indigo-600'
            : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-700'
        ]"
      >
        {{ tab.name }}
        <span v-if="activeTab === tab.id" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
      </button>
    </div>

    <!-- Top bar: ประเภท + แหล่งวัตถุดิบ(ปุ่ม) + ปุ่มตัวกรอง + ปุ่มเพิ่มสูตรคราฟ (สำหรับแอดมิน) -->
    <div class="bg-gray-900 bg-opacity-80 border-b border-gray-800 sticky top-0 z-20">
      <div class="max-w-7xl mx-auto px-3 py-2 flex items-center gap-2">
        <!-- ประเภท (แสดงตลอด) -->
        <select
          v-model="selectedTypeFilter"
          class="bg-gray-700 text-indigo-200 border border-gray-600 rounded-md p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
        >
          <option value="recommended">แนะนำ</option>
          <option value="all">ทั้งหมด</option>
          <option v-for="type in craftTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
        </select>

        <!-- แหล่งวัตถุดิบ: ป้าย + ปุ่มสลับ (อยู่ติดด้านซ้ายของปุ่มตัวกรอง) -->
        <div class="flex items-center gap-2">
          <span class="text-xxs sm:text-xs text-gray-300">แหล่งวัตถุดิบ</span>
          <button
            @click="toggleSource"
            :aria-label="inventorySource === 'storage' ? 'เปลี่ยนเป็นกระเป๋า' : 'เปลี่ยนเป็นคลัง'"
            :title="inventorySource === 'storage' ? 'ตอนนี้: คลัง (กดเพื่อสลับเป็น กระเป๋า)' : 'ตอนนี้: กระเป๋า (กดเพื่อสลับเป็น คลัง)'"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 border',
              inventorySource === 'storage'
                ? 'bg-indigo-600 border-indigo-500 text-white'
                : 'bg-gray-700 border-gray-600 text-indigo-200 hover:bg-gray-600'
            ]"
          >
            <i class="fas" :class="inventorySource === 'storage' ? 'fa-warehouse' : 'fa-shopping-bag'"></i>
            <span class="ml-1">{{ inventorySource === 'storage' ? 'คลัง' : 'กระเป๋า' }}</span>
          </button>
        </div>

        <div class="ml-auto flex items-center gap-2">
          <!-- ปุ่มเพิ่มสูตรคราฟ (เฉพาะแอดมิน) -->
          <button
            v-if="isAdmin"
            @click="showAddCraftModal = true"
            class="px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-500 border border-green-700 text-white text-xs font-semibold transition"
            aria-label="เพิ่มสูตรคราฟใหม่"
            title="เพิ่มสูตรคราฟใหม่"
          >
            <i class="fas fa-plus mr-1 text-xxs"></i>
            เพิ่มสูตรคราฟ
          </button>

          <!-- ปุ่มกาง/ซ่อนตัวกรอง -->
          <button
            @click="toggleFilters()"
            class="px-3 py-1.5 rounded-md bg-gray-800 hover:bg-gray-700 border border-gray-700 text-indigo-200 text-xs font-semibold transition"
            :aria-expanded="showFilters"
            aria-controls="filters-panel"
          >
            <i class="fas fa-sliders-h mr-1 text-xxs"></i>
            {{ showFilters ? 'ซ่อนตัวกรอง' : 'ตัวกรอง' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Filters Panel: ค้นหา -->
    <div v-show="showFilters" id="filters-panel" class="bg-gray-900 bg-opacity-80 p-3 border-b border-gray-800">
      <div class="max-w-7xl mx-auto space-y-2">
        <div class="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <!-- ค้นหา -->
          <div class="flex items-center gap-2 w-full sm:w-auto">
            <label class="text-xs text-gray-400 shrink-0">ค้นหา</label>
            <div class="relative w-full sm:w-72">
              <input
                v-model.trim="searchQuery"
                type="text"
                placeholder="พิมพ์ชื่อ/ประเภท/วัตถุดิบ..."
                class="w-full bg-gray-700 text-indigo-100 placeholder-gray-400 border border-gray-600 rounded-md p-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
              />
              <i class="fas fa-search absolute right-2 top-2.5 text-xs text-gray-400"></i>
            </div>
          </div>
        </div>

        <!-- สรุปผลลัพธ์ -->
        <div class="text-xs text-indigo-200">
          พร้อมคราฟ {{ craftableCount }} / ทั้งหมด {{ totalAfterBaseFilters }}
          <span v-if="searchQuery"> • “{{ searchQuery }}”</span>
        </div>
      </div>
    </div>

    <main>
      <div class="max-w-7xl mx-auto">
        <!-- Alerts -->
        <div v-if="warningMessage" class="bg-gray-900 bg-opacity-80 text-yellow-300 p-3 rounded-md text-center border border-yellow-600 text-sm mt-4">
          {{ warningMessage }}
        </div>
        <div v-if="error" class="bg-gray-900 bg-opacity-80 text-red-300 p-3 rounded-md text-center border border-red-600 text-sm mt-4">
          {{ error }}
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex justify-center items-center h-64 animate-fadeIn">
          <i class="fas fa-spinner text-4xl text-indigo-400 animate-spin-pulse"></i>
        </div>

        <!-- Craft List -->
        <div v-else class="space-y-3 animate-fadeIn">
          <div v-if="activeTab === 'active' && filteredCraftRecipes.length > 0" class="grid grid-cols-1 gap-2">
            <div
              v-for="recipe in filteredCraftRecipes"
              :key="recipe.id"
              @click="!isExpired(recipe) && selectCraftRecipe(recipe)"
              class="bg-gray-900 bg-opacity-80 p-3 rounded-md shadow transition-all duration-200 cursor-pointer flex flex-col border relative hover:bg-gray-800"
              :class="cardBorderClass(recipe)"
            >
              <div v-if="recipe.isRecommended" class="absolute top-2 left-2">
                <i class="fas fa-star text-yellow-400 text-xxs"></i>
              </div>

              <div v-if="isAdmin" class="absolute top-2 right-2 flex space-x-2">
                <button @click.stop="openEditCraftModal(recipe)" class="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition group">
                  <i class="fas fa-edit text-xxs group-hover:scale-110"></i>
                </button>
                <button @click.stop="deleteCraftRecipe(recipe.id)" class="bg-red-600 hover:bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition group">
                  <i class="fas fa-trash text-xxs group-hover:scale-110"></i>
                </button>
              </div>

              <div class="flex items-center gap-3">
                <img
                  :src="recipe.img || 'default-item.png'"
                  alt="Craft Item"
                  class="w-10 h-10 rounded-full object-cover border-2 mr-1"
                  :class="recipe.rarityColors?.borderColor || 'border-indigo-600'"
                  @error="handleImageError"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p
                      class="font-semibold truncate text-sm"
                      :class="recipe.rarityColors?.textColor || 'text-indigo-200'"
                      :title="recipe.name || 'ไม่ระบุ'"
                    >
                      {{ recipe.name || 'ไม่ระบุ' }}
                    </p>
                    <div class="flex flex-wrap gap-1">
                      <span v-if="recipe.maxCrafts" class="px-1.5 py-0.5 rounded-md bg-gray-700 border border-gray-600 text-xxs text-indigo-200">จำนวน{{ getCraftInfo(recipe)?.count || 0 }}/{{ recipe.maxCrafts }}</span>
                    </div>
                  </div>

                  <p class="text-xs text-indigo-300 mt-1 material-list" :title="materialsLine(recipe)">
                    วัสดุ:
                    <span
                      v-for="(material, index) in recipe.materials"
                      :key="material.itemName"
                      :class="hasEnoughMaterial(material) ? 'text-green-400' : 'text-red-400'"
                    >
                      {{ material.name || material.itemName }} {{ getMaterialCount(material.itemName) }}/{{ material.quantity }}<span v-if="index < recipe.materials.length - 1">, </span>
                    </span>
                  </p>

                  <div class="flex items-center gap-2 mt-1">
                    <span v-if="recipe.expirationDate" class="text-xxs text-gray-400">หมดอายุ: {{ formatDate(recipe.expirationDate) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'active'" class="bg-gray-900 bg-opacity-80 p-4 rounded-md text-center text-indigo-300 border border-gray-800 text-sm">
            <p>ไม่มีสูตรคราฟที่ตรงกับตัวกรอง</p>
          </div>

          <div v-if="activeTab === 'expired' && isAdmin" class="space-y-3">
            <div class="flex flex-col sm:flex-row justify-between items-center gap-3 bg-gray-900 bg-opacity-80 p-3 rounded-md border border-gray-800">
              <p class="text-sm text-indigo-200">
                สูตรคราฟหมดอายุที่พบ: <strong class="text-red-300">{{ expiredCraftRecipes.length }}</strong> รายการ
              </p>
              <button
                @click="deleteExpiredCraftRecipes"
                :disabled="isDeletingExpired || expiredCraftRecipes.length === 0"
                class="px-3 py-1.5 rounded-md bg-red-700 hover:bg-red-600 disabled:bg-gray-700 disabled:cursor-not-allowed border border-red-800 text-white text-xs font-semibold transition"
              >
                <span v-if="!isDeletingExpired"><i class="fas fa-trash mr-1 text-xxs"></i> ลบสูตรหมดอายุทั้งหมด</span>
                <span v-else><i class="fas fa-spinner mr-1 text-xxs animate-spin-pulse"></i> กำลังลบ...</span>
              </button>
            </div>

            <div v-if="expiredCraftRecipes.length > 0" class="grid grid-cols-1 gap-2">
              <div
                v-for="recipe in expiredCraftRecipes"
                :key="recipe.id"
                class="bg-gray-900 bg-opacity-80 p-3 rounded-md shadow flex flex-col border border-gray-800 relative opacity-60"
              >
                <div class="absolute top-2 right-2 flex space-x-2">
                  <button @click.stop="openEditCraftModal(recipe)" class="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition group">
                    <i class="fas fa-edit text-xxs group-hover:scale-110"></i>
                  </button>
                  <button @click.stop="deleteCraftRecipe(recipe.id)" class="bg-red-600 hover:bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center transition group">
                    <i class="fas fa-trash text-xxs group-hover:scale-110"></i>
                  </button>
                </div>

                <div class="flex items-center gap-3">
                  <img
                    :src="recipe.img || 'default-item.png'"
                    alt="Craft Item"
                    class="w-10 h-10 rounded-full object-cover border-2 mr-1"
                    :class="recipe.rarityColors?.borderColor || 'border-indigo-600'"
                    @error="handleImageError"
                  />
                  <div class="flex-1 min-w-0 pr-16">
                    <p class="font-semibold truncate text-sm" :class="recipe.rarityColors?.textColor || 'text-indigo-200'">
                      {{ recipe.name || 'ไม่ระบุ' }}
                    </p>
                    <p class="text-xs text-indigo-300 mt-1">ประเภท: {{ recipe.type || 'ไม่ระบุ' }}</p>
                    <p v-if="recipe.expirationDate" class="text-xxs text-red-300 mt-1">หมดอายุ: {{ formatDate(recipe.expirationDate) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="bg-gray-900 bg-opacity-80 p-4 rounded-md text-center text-indigo-300 border border-gray-800 text-sm">
              <p>ไม่มีสูตรคราฟที่หมดอายุ</p>
            </div>
          </div>
        </div>

        <!-- Back -->
        <footer class="mt-4">
          <router-link
            :to="{ name: 'Home' }"
            :class="['inline-flex items-center w-full bg-gray-900 bg-opacity-80 hover:bg-gray-800 text-indigo-200 p-3 justify-center rounded-md font-semibold transition-all duration-200 border border-gray-800 text-sm group']"
          >
            <i class="fas fa-arrow-left mr-1 text-sm group-hover:scale-110 transition-transform duration-200"></i> กลับสู่เมนูหลัก
          </router-link>
        </footer>

        <!-- Modals -->
        <CraftModal
          v-if="showCraftModal && selectedCraftRecipe"
          :recipe="selectedCraftRecipe"
          :total-stats="totalStats"
          :inventory-items="inventoryItems"
          :character-private-data="characterPrivateData"
          :is-crafting="isCrafting"
          :craft-info="getCraftInfo(selectedCraftRecipe)"
          :max-crafts="selectedCraftRecipe.maxCrafts"
          @craft="craftItem"
          @close="closeCraftModal"
        />
        <AddCraftModal
          v-if="isAdmin && showAddCraftModal"
          :craft-types="craftTypes"
          :available-skills="availableSkills"
          :is-adding="isAdding"
          @add="addCraftRecipe"
          @close="showAddCraftModal = false"
        />
        <EditCraftModal
          v-if="isAdmin && showEditCraftModal && selectedCraftRecipe"
          :recipe="selectedCraftRecipe"
          :craft-types="craftTypes"
          :available-skills="availableSkills"
          :is-updating="isUpdating"
          @update="updateCraftRecipe"
          @close="showEditCraftModal = false"
        />
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { isOldSchema, triggerReMigration } from '@/composables/useMigrationGuard';
import CraftModal from './CraftModal.vue';
import AddCraftModal from './AddCraftModal.vue';
import EditCraftModal from './EditCraftModal.vue';
import dayjs from 'dayjs';
import gameItemsData from '@/data/gameItems.json';

export default {
  name: 'CraftList',
  components: { CraftModal, AddCraftModal, EditCraftModal },

  setup() {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const isAdmin = ref(false);

    const LS_KEYS = {
      type: 'craftUI:selectedType',
      filters: 'craftUI:showFilters',
      source: 'craftUI:inventorySource',
      search: 'craftUI:searchQuery',
    };

    const isRestoringPrefs = ref(false);

    const showFilters = ref(false);
    const activeTab = ref('active');
    const selectedTypeFilter = ref('recommended');
    const inventorySource = ref('storage');
    const searchQuery = ref('');

    const craftRecipes = ref([]);
    const expiredCraftRecipes = ref([]);
    const inventoryItems = ref([]);
    const characterPrivateData = ref(null);
    const userCraftData = ref({});
    const loading = ref(true);
    const error = ref(null);
    const warningMessage = ref('');

    const showCraftModal = ref(false);
    const showAddCraftModal = ref(false);
    const showEditCraftModal = ref(false);
    const selectedCraftRecipe = ref(null);
    const isCrafting = ref(false);
    const isAdding = ref(false);
    const isUpdating = ref(false);
    const isDeletingExpired = ref(false);

    let unsubscribeStorage = null;
    let unsubscribeBag = null;
    let unsubscribeCharacterPrivate = null;
    let unsubscribeCraftPurchases = [];

    const craftTypes = [
      { value: 'วัสดุ', label: 'วัสดุ' },
      { value: 'กัปดัก', label: 'กัปดัก' },
      { value: 'ปลดกัปดัก', label: 'ปลดกัปดัก' },
      { value: 'ระเบิด', label: 'ระเบิด' },
      { value: 'หน้ากาก', label: 'หน้ากาก' },
      { value: 'เกราะ', label: 'เกราะ' },
      { value: 'สนับแข้ง', label: 'สนับแข้ง' },
      { value: 'ปลอกแขน', label: 'ปลอกแขน' },
      { value: 'อาวุธสำรอง', label: 'อาวุธสำรอง' },
      { value: 'อาวุธ', label: 'อาวุธ' },
      { value: 'รักษา', label: 'รักษา' },
      { value: 'อาหาร', label: 'อาหาร' },
      { value: 'บุหรี่', label: 'บุหรี่' },
      { value: 'หนังสือ', label: 'หนังสือ' },
      { value: 'หนังสือโลกวิญญาณ', label: 'หนังสือโลกวิญญาณ' },
      { value: 'ไล่ผี', label: 'ไล่ผี' },
      { value: 'พาหนะ', label: 'พาหนะ' },
      { value: 'หุ่นยนต์สังหาร', label: 'หุ่นยนต์สังหาร' },
      { value: 'กล่อง', label: 'กล่อง' },
      { value: 'การ์ดประมวลผลเสริม', label: 'การ์ดประมวลผลเสริม' },
      { value: 'เซ็ตทหาร', label: 'เซ็ตทหาร' },
      { value: 'เซ็ตนักดับเพลิง', label: 'เซ็ตนักดับเพลิง' },
      { value: 'อวัยวะจักรกล', label: 'อวัยวะจักรกล' },
      { value: 'วิญญาณเฝ้าห้อง', label: 'วิญญาณเฝ้าห้อง' },
      { value: 'วิญญาณค้นหาผู้เล่น', label: 'วิญญาณค้นหาผู้เล่น' },
      { value: 'วิญญาณผู้พิทักษ์', label: 'วิญญาณผู้พิทักษ์' },
      { value: 'เครื่องมือช่วยลอบสังหาร', label: 'เครื่องมือช่วยลอบสังหาร' },
      { value: 'ตราอัพเลเวลชาวบ้าน', label: 'ตราอัพเลเวลชาวบ้าน' },
    ];

    const craftTabs = [
      { id: 'active', name: 'รายการคราฟ' },
      { id: 'expired', name: 'หมดอายุ' },
    ];

    const availableSkills = [
      'หนังสือตำราอาหาร',
      'หนังสือวิธีการทำกัปดัก',
      'หนังสือวิธีการทำบุหรี่',
      'หนังสือวิธีการทำหน้ากาก',
      'หนังสือวิธีสร้างอุปกรณ์',
      'หนังสืออุปกรณ์แพทย์',
      'หนังสือคู่มือสร้างมอเตอร์ไซค์',
      'หนังสือนักเขียนมือสมัครเล่น',
      'หนังสือหุ่นยนต์รัสต์แฮมเมอร์',
      'หนังสือหุ่นยนต์ไคเนติ',
      'หนังสือหุ่นยนต์วอยด์',
      'หนังสือหุ่นยนต์รามศึก',
      'หนังสือหุ่นยนต์แอทลาส',
      'หนังสือกัปดักระเบิดตัวตลก',
      'หนังสือกัปดักไวรัส',
      'หนังสือหุ่นยนต์กิ้งก่าพลีชีพ',
      'หนังสือหุ่นยนต์หนูพลีชีพ',
      'หนังสือวิธีทำระเบิดไดนาไมต์',
      'หนังสือวิธีการทำหน้ากากเนฟรอน',
      'หนังสือหุ่นยนต์แมลงสาบไม้พลีชีพ',
      'หนังสือหุ่นยนต์จิ้งจกพลีชีพ',
      'หนังสือหุ่นยนต์แมงมุมพลีชีพ',
      'หนังสือกัปดักห่วงเชือกลาก',
      'หนังสือกัปดักผ้าคลุมรัด',
      'หนังสือระเบิดประทัด',
      'หนังสือระเบิดขวดไฟ',
      'หนังสือระเบิดพลาสติกแบบหยาบ',
      'หนังสือวิธีสร้างเหล็กกล้า',
      'หนังสือระเบิดทีเอ็นที',
      'หนังสือระเบิดทีเอ็นที',
      'หนังสือกัปดักเลเซอร์',
      'หนังสือหุ่นยนต์ตะขาบเหล็กพลีชีพ',
      'หนังสือระเบิดซีโฟร์',
      'หนังสือกัปดักสนามแม่เหล็กไฟฟ้า',
      'หนังสือหุ่นยนต์งูเหล็กพลีชีพ',
      'หนังสือกัปดักเลเซอร์',
      'หนังสือวิธีการทำพลังงานไวรัส',
      'หนังสือหมวกทหาร',
    ];

    const totalStats = computed(() => {
      if (!characterPrivateData.value) return { int: 0 };

      const s = characterPrivateData.value;

      return {
        int:
          (s.itemStats?.int || 0) +
          (s.bonusStats?.int || 0) +
          (s.stats?.int || 0) +
          (s.traitStats?.int || 0) +
          (s.skillStats?.int || 0) +
          (s.titleStats?.int || 0) +
          (s.tattooStats?.int || 0) +
          (s.ghostStats?.int || 0),
      };
    });

    const formatDate = (d) => {
      if (!d) return 'ไม่มีกำหนด';

      const date = dayjs(d);

      if (!date.isValid()) return 'วันที่ไม่ถูกต้อง';

      return date.format('DD/MM/YYYY HH:mm');
    };

    const isExpired = (recipe) => {
      if (!recipe?.expirationDate) return false;

      return dayjs().isAfter(dayjs(recipe.expirationDate));
    };

    const hasRequiredSkill = (requiredSkill) => {
      if (!requiredSkill || !characterPrivateData.value?.learnedSkills) return true;

      return characterPrivateData.value.learnedSkills.includes(requiredSkill);
    };

    const getCraftInfo = (recipe) => {
      if (!recipe?.id) return { count: 0, max: null };

      return userCraftData.value[recipe.id] || {
        count: 0,
        max: recipe.maxCrafts || null,
      };
    };

    const handleImageError = (e) => {
      e.target.src = 'default-item.png';
    };

    const materialCounts = computed(() => {
      const counts = {};

      inventoryItems.value.forEach((item) => {
        const templateId = item.templateId || item.name;

        if (templateId) {
          counts[templateId] = (counts[templateId] || 0) + 1;
        }

        if (item.name && item.name !== templateId) {
          counts[item.name] = (counts[item.name] || 0) + 1;
        }
      });

      return counts;
    });

    const getMaterialCount = (itemName) => {
      return materialCounts.value[itemName] || 0;
    };

    const hasEnoughMaterials = (recipe) => {
      return recipe?.materials?.every((m) => {
        return (materialCounts.value[m.itemName] || 0) >= m.quantity;
      }) ?? false;
    };

    const hasEnoughMaterial = (m) => {
      return (materialCounts.value[m.itemName] || 0) >= m.quantity;
    };

    const hasEnoughInt = (requiredInt) => {
      return totalStats.value.int >= (requiredInt || 0);
    };

    const withinMaxCrafts = (r) => {
      if (!r?.maxCrafts) return true;

      const c = getCraftInfo(r)?.count || 0;

      return c < r.maxCrafts;
    };

    const canCraft = (r) => {
      return (
        !isExpired(r) &&
        hasEnoughMaterials(r) &&
        hasEnoughInt(r.requiredInt) &&
        (!r.requiredSkill || hasRequiredSkill(r.requiredSkill)) &&
        withinMaxCrafts(r)
      );
    };

    const baseFiltered = computed(() => {
      let list = craftRecipes.value.filter((r) => !isExpired(r));

      const q = (searchQuery.value || '').toLowerCase();

      if (q) {
        list = list.filter((r) => {
          const inName = (r.name || '').toLowerCase().includes(q);
          const inType = (r.type || '').toLowerCase().includes(q);
          const inMats = (r.materials || []).some((m) => {
            return ((m.name || m.itemName || '') + '').toLowerCase().includes(q);
          });

          return inName || inType || inMats;
        });
      }

      return list;
    });

    const filteredCraftRecipes = computed(() => {
      const list = [...baseFiltered.value];

      list.sort((a, b) => {
        const priorityA = canCraft(a) ? 2 : hasEnoughMaterials(a) ? 1 : 0;
        const priorityB = canCraft(b) ? 2 : hasEnoughMaterials(b) ? 1 : 0;

        if (priorityA !== priorityB) return priorityB - priorityA;

        return (a.level || 1) - (b.level || 1);
      });

      return list;
    });

    const craftableCount = computed(() => {
      return baseFiltered.value.filter((r) => canCraft(r)).length;
    });

    const totalAfterBaseFilters = computed(() => {
      return baseFiltered.value.length;
    });

    const loadPrefs = () => {
      isRestoringPrefs.value = true;

      try {
        const t = localStorage.getItem(LS_KEYS.type);
        if (t) selectedTypeFilter.value = t;

        const f = localStorage.getItem(LS_KEYS.filters);
        if (f !== null) showFilters.value = f === '1';

        const src = localStorage.getItem(LS_KEYS.source);
        if (src && (src === 'storage' || src === 'bag')) {
          inventorySource.value = src;
        }

        const q = localStorage.getItem(LS_KEYS.search);
        if (q !== null) searchQuery.value = q;
      } catch (_) {}
    };

    const save = (k, v) => {
      try {
        localStorage.setItem(k, v);
      } catch (_) {}
    };

    watch(selectedTypeFilter, async (v) => {
      save(LS_KEYS.type, v);

      if (isRestoringPrefs.value) return;

      await fetchCurrentCraftTab();
    });

    watch(activeTab, async () => {
      if (activeTab.value === 'expired' && !isAdmin.value) {
        activeTab.value = 'active';
        return;
      }

      loading.value = true;
      await fetchCurrentCraftTab();
    });

    watch(isAdmin, (admin) => {
      if (!admin && activeTab.value === 'expired') {
        activeTab.value = 'active';
      }
    });

    watch(showFilters, (v) => {
      save(LS_KEYS.filters, v ? '1' : '0');
    });

    watch(searchQuery, (v) => {
      save(LS_KEYS.search, v);
    });

    watch(inventorySource, (v, old) => {
      save(LS_KEYS.source, v);

      if (isRestoringPrefs.value) return;

      if (old) {
        switchInventoryWatcher(old, v);
      }
    });

    const toggleFilters = () => {
      showFilters.value = !showFilters.value;
    };

    const toggleSource = () => {
      inventorySource.value = inventorySource.value === 'storage' ? 'bag' : 'storage';
    };

    const checkAdminStatus = () => {
      if (!user.value) {
        isAdmin.value = false;
        loading.value = false;
        return;
      }

      isAdmin.value = playerStore.userData?.admin === true;
    };

    const subscribeInventory = () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอิน';
        loading.value = false;
        return null;
      }

      const col = inventorySource.value === 'storage' ? 'storages' : 'bags';

      return projectFirestore
        .collection(col)
        .doc(user.value.uid)
        .onSnapshot(
          async (doc) => {
            if (doc.exists) {
              const docData = doc.data();

              if (isOldSchema(docData)) {
                await triggerReMigration();
                return;
              }

              const itemsObj = docData.items || {};

              inventoryItems.value = Object.entries(itemsObj)
                .map(([instanceId, itemData]) => {
                  const template = gameItemsData[itemData.templateId] || {};
                  const states = itemData.states || {};

                  return {
                    id: instanceId,
                    instanceId,
                    templateId: itemData.templateId,
                    states,
                    ...template,
                    ...states,
                    name: template.name || itemData.templateId,
                    type: states.type || template.type || '',
                    transfer: states.transfer ?? template.transfer ?? false,
                    locked: states.locked || false,
                  };
                })
                .filter((i) => i.templateId);

              loading.value = false;
            } else {
              inventoryItems.value = [];
              error.value = `ไม่พบข้อมูล${inventorySource.value === 'storage' ? 'คลังเก็บ' : 'กระเป๋า'}`;
              loading.value = false;
            }
          },
          (err) => {
            console.error(`Error fetching ${col}:`, err);
            error.value = `เกิดข้อผิดพลาดในการดึงข้อมูล${inventorySource.value === 'storage' ? 'คลังเก็บ' : 'กระเป๋า'}`;
            loading.value = false;
          }
        );
    };

    const switchInventoryWatcher = (from, to) => {
      if (from === 'storage' && unsubscribeStorage) {
        unsubscribeStorage();
        unsubscribeStorage = null;
      }

      if (from === 'bag' && unsubscribeBag) {
        unsubscribeBag();
        unsubscribeBag = null;
      }

      const unsub = subscribeInventory();

      if (to === 'storage') {
        unsubscribeStorage = unsub;
      } else {
        unsubscribeBag = unsub;
      }
    };

    const buildCraftBaseQuery = () => {
      let q = projectFirestore.collection('craftRecipes');

      if (selectedTypeFilter.value === 'recommended') {
        q = q.where('isRecommended', '==', true);
      } else if (selectedTypeFilter.value !== 'all') {
        q = q.where('type', '==', selectedTypeFilter.value);
      }

      return q;
    };

    const normalizeRecipeDoc = (doc) => {
      try {
        const recipe = doc.data();
        const itemData = gameItemsData[recipe.name];

        if (!itemData) {
          console.warn(`[CraftList] item not found in gameItemsData: ${recipe.name}`, doc.id);
          return null;
        }

        const materials = (recipe.materials || []).map((m) => {
          const matData = gameItemsData[m.itemName] || {};

          return {
            itemName: m.itemName,
            quantity: m.quantity,
            name: matData.name || m.itemName,
            img: matData.img || 'default-item.png',
            rarityColors: matData.rarityColors || {
              borderColor: 'border-indigo-600',
            },
          };
        });

        return {
          id: doc.id,
          ...itemData,
          type: recipe.type || '',
          level: recipe.level || 1,
          materials,
          requiredInt: recipe.requiredInt || 0,
          requiredSkill: recipe.requiredSkill || null,
          maxCrafts: recipe.maxCrafts || null,
          isRecommended: recipe.isRecommended || false,
          expirationDate: recipe.expirationDate || null,
          transferChance: recipe.transferChance || 0,
          successChance: recipe.successChance || 100,
        };
      } catch (err) {
        console.error(`Error processing recipe ${doc.id}:`, err);
        return null;
      }
    };

    const fetchCraftRecipes = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอิน';
        loading.value = false;
        return;
      }

      loading.value = true;

      try {
        const nowIso = new Date().toISOString();

        const noExpirationQuery = buildCraftBaseQuery()
          .where('expirationDate', '==', null);

        const activeExpirationQuery = buildCraftBaseQuery()
          .where('expirationDate', '>', nowIso)
          .orderBy('expirationDate', 'asc');

        const queryTasks = [
          noExpirationQuery.get(),
          activeExpirationQuery.get(),
        ];

        const results = await Promise.all(queryTasks);

        const noExpirationSnapshot = results[0];
        const activeExpirationSnapshot = results[1];

        const docsMap = new Map();

        noExpirationSnapshot.docs.forEach((doc) => {
          docsMap.set(doc.id, doc);
        });

        activeExpirationSnapshot.docs.forEach((doc) => {
          docsMap.set(doc.id, doc);
        });

        console.log('[CraftList] craftRecipes loaded docs:', docsMap.size);

        const fetchedRecipes = Array.from(docsMap.values())
          .map(normalizeRecipeDoc)
          .filter((r) => r)
          .filter((r) => !isExpired(r))
          .sort((a, b) => {
            const levelA = a.level || 1;
            const levelB = b.level || 1;

            if (levelA !== levelB) return levelA - levelB;

            return (a.name || '').localeCompare(b.name || '');
          });

        craftRecipes.value = fetchedRecipes;
        expiredCraftRecipes.value = [];
        fetchUserCraftData(fetchedRecipes.map((recipe) => recipe.id));

        loading.value = false;
        error.value = craftRecipes.value.length ? null : 'ไม่มีสูตรคราฟในขณะนี้';
      } catch (err) {
        console.error('Error fetching craft recipes:', err);

        if (err?.code === 'failed-precondition') {
          error.value = 'Firestore ต้องสร้าง composite index สำหรับ query สูตรคราฟนี้ กรุณาดู link index ใน console';
        } else {
          error.value = 'เกิดข้อผิดพลาดในการดึงสูตรคราฟ';
        }

        loading.value = false;
      }
    };

    const fetchExpiredCraftRecipes = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอิน';
        loading.value = false;
        return;
      }

      if (!isAdmin.value) {
        activeTab.value = 'active';
        loading.value = false;
        return;
      }

      loading.value = true;
      clearCraftPurchaseListeners();
      userCraftData.value = {};

      try {
        const nowIso = new Date().toISOString();
        const expiredSnapshot = await buildCraftBaseQuery()
          .where('expirationDate', '<=', nowIso)
          .orderBy('expirationDate', 'asc')
          .get();

        console.log('[CraftList] craftRecipes loaded docs:', expiredSnapshot.size);

        expiredCraftRecipes.value = expiredSnapshot.docs
          .map(normalizeRecipeDoc)
          .filter((r) => r)
          .sort((a, b) => dayjs(b.expirationDate || 0).diff(dayjs(a.expirationDate || 0)));

        loading.value = false;
        error.value = null;
      } catch (err) {
        console.error('Error fetching expired craft recipes:', err);
        error.value = err?.code === 'failed-precondition'
          ? 'Firestore ต้องสร้าง composite index สำหรับ query สูตรคราฟหมดอายุ กรุณาดู link index ใน console'
          : 'เกิดข้อผิดพลาดในการดึงสูตรคราฟหมดอายุ';
        loading.value = false;
      }
    };

    const fetchCurrentCraftTab = async () => {
      if (activeTab.value === 'expired') {
        await fetchExpiredCraftRecipes();
        return;
      }

      await fetchCraftRecipes();
    };

    const fetchCharacterPrivateData = () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอิน';
        loading.value = false;
        return;
      }

      unsubscribeCharacterPrivate = projectFirestore
        .collection('characterPrivate')
        .doc(user.value.uid)
        .onSnapshot(
          (doc) => {
            if (doc.exists) {
              const d = doc.data();

              characterPrivateData.value = {
                ...d,
                itemStats: d.itemStats || { int: 0 },
                bonusStats: d.bonusStats || { int: 0 },
                stats: d.stats || { int: 0 },
                traitStats: d.traitStats || { int: 0 },
                skillStats: d.skillStats || { int: 0 },
                titleStats: d.titleStats || { int: 0 },
                tattooStats: d.tattooStats || { int: 0 },
                ghostStats: d.ghostStats || { int: 0 },
                learnedSkills: d.learnedSkills || [],
              };
            } else {
              characterPrivateData.value = null;
              error.value = 'ไม่พบข้อมูลส่วนตัวของตัวละคร';
            }
          },
          (err) => {
            console.error('Error fetching character private data:', err);
            error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลตัวละคร';
          }
        );
    };

    const clearCraftPurchaseListeners = () => {
      unsubscribeCraftPurchases.forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });

      unsubscribeCraftPurchases = [];
    };

    const fetchUserCraftData = (visibleCraftIds = craftRecipes.value.map((recipe) => recipe.id)) => {
      if (!user.value) return;

      const userId = user.value.uid;
      const uniqueCraftIds = [...new Set(visibleCraftIds.filter(Boolean))];

      clearCraftPurchaseListeners();
      userCraftData.value = {};

      console.log('[CraftList] craftPurchases loaded docs:', uniqueCraftIds.length);

      if (uniqueCraftIds.length === 0) {
        return;
      }

      const craftDataById = {};
      const updateCraftData = () => {
        userCraftData.value = { ...craftDataById };
      };

      try {
        uniqueCraftIds.forEach((craftId) => {
          const docId = `${userId}_${craftId}`;
          const unsubscribe = projectFirestore
            .collection('craftPurchases')
            .doc(docId)
            .onSnapshot(
              (doc) => {
                if (doc.exists) {
                  const data = doc.data();

                  craftDataById[craftId] = {
                    count: data.count || 0,
                    max: data.max || null,
                  };
                } else {
                  delete craftDataById[craftId];
                }

                updateCraftData();
              },
              (err) => {
                console.error('Error fetching direct craft purchase doc:', err);
                error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลการคราฟ';
              }
            );

          unsubscribeCraftPurchases.push(unsubscribe);
        });
      } catch (err) {
        console.error('Error setting up craft purchases listeners:', err);
        error.value = 'เกิดข้อผิดพลาดในการเชื่อมต่อฐานข้อมูลการคราฟ';
      }
    };

    const selectCraftRecipe = (r) => {
      selectedCraftRecipe.value = r;
      showCraftModal.value = true;
    };

    const craftItem = async (quantity) => {
      if (!user.value || !selectedCraftRecipe.value?.id) {
        warningMessage.value = 'กรุณาเลือกสูตรคราฟ';
        return;
      }

      const itemIds = [];

      for (const material of selectedCraftRecipe.value.materials) {
        const needed = material.quantity * quantity;
        let count = 0;

        for (const item of inventoryItems.value) {
          if (
            (item.templateId === material.itemName || item.name === material.itemName) &&
            count < needed
          ) {
            itemIds.push(item.id);
            count++;
          }
        }

        if (count < needed) {
          warningMessage.value = `วัตถุดิบ "${material.itemName}" ไม่เพียงพอ ต้องการ ${needed} มี ${count}`;
          return;
        }
      }

      isCrafting.value = true;

      const timeout = setTimeout(() => {
        isCrafting.value = false;
        warningMessage.value = 'การคราฟใช้เวลานานเกินไป';
      }, 10000);

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');

        const result = await gameActions({
          action: 'craftItem',
          craftId: selectedCraftRecipe.value.id,
          materialInstanceIds: itemIds,
          source: inventorySource.value,
          quantity,
        });

        warningMessage.value = result.data.message;
        closeCraftModal();
      } catch (err) {
        console.error('Error crafting item:', err);
        warningMessage.value = err.message || 'เกิดข้อผิดพลาดในการคราฟ';
      } finally {
        clearTimeout(timeout);
        isCrafting.value = false;
      }
    };

    const deleteCraftRecipe = async (recipeId) => {
      if (!isAdmin.value) {
        error.value = 'คุณไม่มีสิทธิ์ลบสูตรคราฟ';
        return;
      }

      try {
        await projectFirestore.collection('craftRecipes').doc(recipeId).delete();
        warningMessage.value = 'ลบสูตรคราฟสำเร็จ!';

        await fetchCurrentCraftTab();
      } catch (err) {
        console.error('Error deleting craft recipe:', err);
        error.value = 'เกิดข้อผิดพลาดในการลบสูตรคราฟ';
      }
    };

    const deleteExpiredCraftRecipes = async () => {
      if (!isAdmin.value) {
        error.value = 'คุณไม่มีสิทธิ์ลบสูตรคราฟหมดอายุ';
        return;
      }

      if (expiredCraftRecipes.value.length === 0) return;

      const confirmed = window.confirm(`ต้องการลบสูตรคราฟหมดอายุที่แสดงอยู่ ${expiredCraftRecipes.value.length} รายการหรือไม่?`);
      if (!confirmed) return;

      isDeletingExpired.value = true;
      error.value = '';
      warningMessage.value = '';

      try {
        const nowIso = new Date().toISOString();
        let deletedCount = 0;

        while (true) {
          const snapshot = await buildCraftBaseQuery()
            .where('expirationDate', '<=', nowIso)
            .limit(500)
            .get();

          if (snapshot.empty) break;

          const batch = projectFirestore.batch();

          snapshot.docs.forEach((doc) => {
            batch.delete(doc.ref);
          });

          await batch.commit();

          deletedCount += snapshot.size;

          if (snapshot.size < 500) break;
        }

        warningMessage.value = `ลบสูตรคราฟหมดอายุสำเร็จ ${deletedCount} รายการ`;
        await fetchCurrentCraftTab();
      } catch (err) {
        console.error('Error deleting expired craft recipes:', err);
        error.value = 'เกิดข้อผิดพลาดในการลบสูตรคราฟหมดอายุ';
      } finally {
        isDeletingExpired.value = false;
      }
    };

    const addCraftRecipe = async (data) => {
      if (!isAdmin.value) {
        error.value = 'คุณไม่มีสิทธิ์เพิ่มสูตรคราฟ';
        return;
      }

      if (
        !data.name?.trim() ||
        !data.type ||
        data.level < 1 ||
        data.materials.some((m) => !m.itemName?.trim() || m.quantity <= 0) ||
        data.requiredInt < 0 ||
        data.transferChance < 0 ||
        data.transferChance > 100 ||
        data.successChance < 0 ||
        data.successChance > 100
      ) {
        error.value = 'กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง';
        return;
      }

      isAdding.value = true;

      const timeout = setTimeout(() => {
        isAdding.value = false;
        error.value = 'การเพิ่มสูตรใช้เวลานานเกินไป';
      }, 10000);

      try {
        const itemData = gameItemsData[data.name.trim()];

        if (!itemData) {
          error.value = 'ไม่พบไอเทมนี้';
          return;
        }

        await projectFirestore.collection('craftRecipes').add({
          name: data.name.trim(),
          type: data.type,
          level: data.level,
          materials: data.materials.map((m) => ({
            itemName: m.itemName.trim(),
            quantity: m.quantity,
          })),
          requiredInt: data.requiredInt,
          requiredSkill: data.requiredSkill || null,
          maxCrafts: data.maxCrafts || null,
          isRecommended: data.isRecommended,
          expirationDate: data.expirationDate
            ? new Date(data.expirationDate).toISOString()
            : null,
          transferChance: data.transferChance,
          successChance: data.successChance,
          createdAt: new Date(),
        });

        warningMessage.value = 'เพิ่มสูตรคราฟสำเร็จ!';
        showAddCraftModal.value = false;

        await fetchCurrentCraftTab();
      } catch (err) {
        console.error('Error adding craft recipe:', err);
        error.value = 'เกิดข้อผิดพลาดในการเพิ่มสูตรคราฟ';
        isAdding.value = false;
      } finally {
        clearTimeout(timeout);
        isAdding.value = false;
      }
    };

    const openEditCraftModal = (r) => {
      selectedCraftRecipe.value = r;
      showEditCraftModal.value = true;
    };

    const updateCraftRecipe = async (data) => {
      if (!isAdmin.value) {
        error.value = 'คุณไม่มีสิทธิ์แก้ไขสูตรคราฟ';
        return;
      }

      if (
        !data.id ||
        !data.type ||
        data.level < 1 ||
        data.materials.some((m) => !m.itemName?.trim() || m.quantity <= 0) ||
        data.requiredInt < 0 ||
        data.transferChance < 0 ||
        data.transferChance > 100 ||
        data.successChance < 0 ||
        data.successChance > 100
      ) {
        error.value = 'กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง';
        return;
      }

      isUpdating.value = true;

      const timeout = setTimeout(() => {
        isUpdating.value = false;
        error.value = 'การแก้ไขสูตรใช้เวลานานเกินไป';
      }, 10000);

      try {
        await projectFirestore.collection('craftRecipes').doc(data.id).update({
          type: data.type,
          level: data.level,
          materials: data.materials.map((m) => ({
            itemName: m.itemName.trim(),
            quantity: m.quantity,
          })),
          requiredInt: data.requiredInt,
          requiredSkill: data.requiredSkill || null,
          maxCrafts: data.maxCrafts || null,
          isRecommended: data.isRecommended,
          expirationDate: data.expirationDate
            ? new Date(data.expirationDate).toISOString()
            : null,
          transferChance: data.transferChance,
          successChance: data.successChance,
          updatedAt: new Date(),
        });

        warningMessage.value = 'แก้ไขสูตรคราฟสำเร็จ!';
        showEditCraftModal.value = false;

        await fetchCurrentCraftTab();
      } catch (err) {
        console.error('Error updating craft recipe:', err);
        error.value = 'เกิดข้อผิดพลาดในการแก้ไขสูตรคราฟ';
      } finally {
        clearTimeout(timeout);
        isUpdating.value = false;
      }
    };

    const closeCraftModal = () => {
      showCraftModal.value = false;
      selectedCraftRecipe.value = null;
    };

    const startInventorySubscription = () => {
      const unsub = subscribeInventory();

      if (inventorySource.value === 'storage') {
        unsubscribeStorage = unsub;
      } else {
        unsubscribeBag = unsub;
      }
    };

    onMounted(async () => {
      loadPrefs();

      await nextTick();

      isRestoringPrefs.value = false;

      checkAdminStatus();
      startInventorySubscription();
      fetchCurrentCraftTab();
      fetchCharacterPrivateData();
    });

    onUnmounted(() => {
      if (unsubscribeStorage) unsubscribeStorage();
      if (unsubscribeBag) unsubscribeBag();
      if (unsubscribeCharacterPrivate) unsubscribeCharacterPrivate();

      clearCraftPurchaseListeners();
    });

    return {
      isAdmin,
      activeTab,

      showFilters,
      selectedTypeFilter,
      inventorySource,
      searchQuery,

      craftRecipes,
      expiredCraftRecipes,
      inventoryItems,
      characterPrivateData,
      userCraftData,

      loading,
      error,
      warningMessage,

      showCraftModal,
      showAddCraftModal,
      showEditCraftModal,

      selectedCraftRecipe,
      isCrafting,
      isAdding,
      isUpdating,
      isDeletingExpired,

      craftTabs,
      craftTypes,
      availableSkills,

      filteredCraftRecipes,
      craftableCount,
      totalAfterBaseFilters,
      totalStats,

      formatDate,
      isExpired,
      getCraftInfo,
      handleImageError,
      getMaterialCount,
      hasEnoughMaterials,
      hasEnoughMaterial,
      hasEnoughInt,
      hasRequiredSkill,
      canCraft,

      materialsLine: (r) => {
        return (r.materials || [])
          .map((m) => `${m.name || m.itemName} ${getMaterialCount(m.itemName)}/${m.quantity}`)
          .join(', ');
      },

      cardBorderClass: (r) => {
        if (isExpired(r)) return 'border-2 border-gray-700 opacity-50 cursor-not-allowed';
        if (canCraft(r)) return 'border-2 border-green-800';
        if (r.isRecommended) return 'border-2 border-yellow-800';

        const partly =
          hasEnoughMaterials(r) ||
          hasEnoughInt(r.requiredInt) ||
          hasRequiredSkill(r.requiredSkill);

        return partly ? 'border-2 border-yellow-900' : 'border-2 border-red-800';
      },

      toggleFilters,
      toggleSource,

      selectCraftRecipe,
      craftItem,
      deleteCraftRecipe,
      deleteExpiredCraftRecipes,
      addCraftRecipe,
      openEditCraftModal,
      updateCraftRecipe,
      closeCraftModal,
    };
  },
};
</script>

<style scoped>
/* thin scrollbar */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background:#2d3748; border-radius:10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background:#4a5568; border-radius:10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background:#6b7280; }
.scrollbar-thin { scrollbar-width:thin; scrollbar-color:#4a5568 #2d3748; }

/* animations */
@keyframes modalFade { from{opacity:0;} to{opacity:1;} }
.animate-modalFade { animation: modalFade .3s ease-out; }
@keyframes fadeIn { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
.animate-fadeIn { animation: fadeIn .3s ease-out; }
@keyframes spin-pulse { 0%{transform:rotate(0);} 100%{transform:rotate(360deg);} }
.animate-spin-pulse { animation: spin-pulse 1s infinite linear; }

/* tiny icon size */
.text-xxs { font-size: 0.65rem; }

/* ปรับแต่งการแสดงผลวัสดุ */
.material-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem; /* ระยะห่างระหว่างวัสดุ */
}

/* ปรับขนาดตัวอักษรสำหรับรายการวัสดุยาวๆ */
.material-list span {
  font-size: 0.65rem; /* ขนาดเล็กลงเล็กน้อย */
}
</style>
